import { Provider } from '@angular/core';
import { environment } from 'src/environments/environment';
// Runtime dependency runs one way only: store-config → entity-config.
// entity-config imports nothing but the EntityId TYPE from here (erased at
// compile time), so this import cannot complete a cycle.
import { entityById, isVatRegisteredAt, SellerEntity } from './entity-config';
import { LazadaAuthInfo } from '../services/lazada/interfaces/lazada-delegate-request';
import { SsmAuthInfo } from '../services/shopee/interfaces/shopee-delegate-request';
import { TiktokAuthInfo } from '../services/tiktok/interfaces/tiktok-delegate-request';

// Single source of truth for the five stores this console operates: their ids,
// display titles, credential locations and owning taxpayer. Transcribed from
// vat-regis-plan/store-entity-config-current.yaml on 2026-08-05 — that file is
// the planning document, this one is what ships, and the two are meant to stay
// in step.
//
// storeId identifies the shop to the billing-request collector, which keys
// records on (storeId, orderSn). It is not ours to invent: the collector
// rejects a write naming a store missing from GET /v1/stores with 422
// STORE_UNKNOWN, and every writer — this app, the chatbot, the public billing
// form — must name a shop identically or one order becomes two records and two
// billing documents get issued. The other writers live in separate repos, so
// nothing here can enforce that. All five ids below were checked against
// GET /v1/stores on 2026-08-05 and matched exactly; that endpoint is the
// authority if they ever diverge.

export type LazadaStoreId = 'lazada-genlogic' | 'lazada-gen4group';
export type ShopeeStoreId = 'shopee-general4' | 'shopee-genlogic';
export type TiktokStoreId = 'tiktok-genlogic';
export type StoreId = LazadaStoreId | ShopeeStoreId | TiktokStoreId;

// The taxpayer whose sales a store's orders are. Consumed by the finance
// module's revenue attribution and by `sellerFor` below — this mapping is what
// derives an invoice's seller from where the sale came from, so it is the one
// assignment that must never be guessed from a store's name. The legal names
// and tax ids live in entity-config.ts.
export type EntityId = 'genlogic-co' | 'disorn' | 'pensri';

// The SSM namespace a store's API credentials live under. This is the only
// irreducible fact per store — every parameter name is derived from it — and
// so the only thing to change if a namespace is ever renamed.
//
// Note that two of these do not match the store they serve: lazada-genlogic
// and tiktok-genlogic both read from /arduino4/. That is how the parameters are
// actually named in SSM today; renaming them is a coordinated change across
// this repo and the delegate Lambdas, not a rename here.
const SSM_NAMESPACE: Record<StoreId, string> = {
    'lazada-genlogic': '/arduino4/',
    'lazada-gen4group': '/gen4group/',
    'shopee-general4': '/arduino4/',
    'shopee-genlogic': '/genlogic/',
    'tiktok-genlogic': '/arduino4/',
};

// Credentials are stored one parameter per field at
// `<namespace><platform>/<field>` — e.g. /arduino4/lazada/app_key. The
// namespaces above carry their own trailing slash so they transcribe from the
// plan file unchanged; this function is the only place that relies on it.
const param = (storeId: StoreId, platform: string, field: string): string =>
    `${SSM_NAMESPACE[storeId]}${platform}/${field}`;

export const lazadaAuthInfoFor = (storeId: LazadaStoreId): LazadaAuthInfo => ({
    appKeyName: param(storeId, 'lazada', 'app_key'),
    appSecretName: param(storeId, 'lazada', 'app_secret'),
    accessTokenName: param(storeId, 'lazada', 'access_token'),
});

export const shopeeAuthInfoFor = (storeId: ShopeeStoreId): SsmAuthInfo => ({
    shopIdName: param(storeId, 'shopee', 'shop_id'),
    partnerIdName: param(storeId, 'shopee', 'partner_id'),
    partnerKeyName: param(storeId, 'shopee', 'partner_key'),
    accessTokenName: param(storeId, 'shopee', 'access_token'),
});

export const tiktokAuthInfoFor = (storeId: TiktokStoreId): TiktokAuthInfo => ({
    appKeyName: param(storeId, 'tiktok', 'app_key'),
    appSecretName: param(storeId, 'tiktok', 'app_secret'),
    accessTokenName: param(storeId, 'tiktok', 'access_token'),
    shopCipherName: param(storeId, 'tiktok', 'shop_cipher'),
});

// Re-authorising a Lazada store needs three parameters beyond the set used to
// sign calls: where Lazada redirects back to, and where the resulting auth code
// and refresh token are written.
export interface LazadaOAuthParams {
    callbackUrlKey: string;
    appKeyKey: string;
    appSecretKey: string;
    accessTokenKey: string;
    refreshTokenKey: string;
    authCodeKey: string;
}

export const lazadaOAuthParamsFor = (
    storeId: LazadaStoreId,
): LazadaOAuthParams => ({
    callbackUrlKey: param(storeId, 'lazada', 'callback_url'),
    appKeyKey: param(storeId, 'lazada', 'app_key'),
    appSecretKey: param(storeId, 'lazada', 'app_secret'),
    accessTokenKey: param(storeId, 'lazada', 'access_token'),
    refreshTokenKey: param(storeId, 'lazada', 'refresh_token'),
    authCodeKey: param(storeId, 'lazada', 'auth_code'),
});

// What a store prints as the seller on its invoices and as the sender on its
// shipping labels.
export interface StorePrintInfo {
    // Invoice seller name, and shipping-label sender name. The trading name the
    // buyer recognises rather than a legal entity name — ordinary invoices are
    // not tax invoices and do not have to carry one.
    name: string;
    // Invoice seller address, and shipping-label sender address.
    address: string;
    // Storefront URL printed on the invoice.
    website: string;
    // The seller's tax registration. Stamped on the invoice only when the
    // customer supplies a tax ID of their own, which is what turns the document
    // into a tax receipt.
    taxId: string;
    // Printed alongside taxId, under the same condition.
    branch: string;
}

interface StoreConfigCommon {
    title: string;
    entityId: EntityId;
    // Which registered taxpayer issues the tax-invoice DOCUMENT when a
    // customer explicitly asks for one on a store whose owner is not VAT
    // registered. Business decision 2026-08: the company issues for
    // requesters on shopee-genlogic — the document belongs to genlogic-co's
    // output tax while the sale's revenue stays the owner's (finance
    // attribution keeps using entityId). Absent means no override — a
    // request on such a store keeps the plain invoice path.
    requestedTaxInvoiceEntityId?: EntityId;
    printInfo: StorePrintInfo;
}

export interface LazadaStoreConfig extends StoreConfigCommon {
    platform: 'lazada';
    storeId: LazadaStoreId;
    authInfo: LazadaAuthInfo;
}

export interface ShopeeStoreConfig extends StoreConfigCommon {
    platform: 'shopee';
    storeId: ShopeeStoreId;
    authInfo: SsmAuthInfo;
    // The shop id Shopee itself knows this store by, as recorded in the
    // billing-request registry. Informational — the value used to sign calls is
    // read from SSM at request time.
    platformShopId?: number;
    // The store cannot schedule courier pickups; ship actions always arrange a
    // drop off instead.
    dropOffOnly?: boolean;
}

export interface TiktokStoreConfig extends StoreConfigCommon {
    platform: 'tiktok';
    storeId: TiktokStoreId;
    authInfo: TiktokAuthInfo;
}

export type StoreConfig =
    LazadaStoreConfig | ShopeeStoreConfig | TiktokStoreConfig;

// The factories exist so authInfo cannot drift from storeId, and so the
// seller tax registration cannot drift from the owning entity: a store
// declares its id and entity once and both follow from that. What a store
// prints as its brand is still spelled out per store below — there are
// deliberately no defaults to inherit, so adding a store without saying what
// it prints is a compile error rather than a silent copy of another store's
// identity.
//
// `taxId` and `branch` are NOT declarable per store. They are the owning
// taxpayer's identity (entity-config.ts) — the hand-copied per-store taxId
// values this replaces are how one individual's id ended up stamped on four
// stores of two different taxpayers (CONSOLE-BUILD-SPEC P1), and the ''
// branch every store used to declare printed a blank where a tax receipt
// must say สำนักงานใหญ่ (spec W13).
type StorePrintDeclaration = Omit<StorePrintInfo, 'taxId' | 'branch'>;

type StoreDeclaration<T extends StoreConfigCommon> = Omit<
    T,
    'platform' | 'authInfo' | 'printInfo'
> & { printInfo: StorePrintDeclaration };

const entityPrintInfo = (
    entityId: EntityId,
    printInfo: StorePrintDeclaration,
): StorePrintInfo => {
    const entity = entityById(entityId);
    return {
        ...printInfo,
        taxId: entity.taxId,
        branch: entity.branch,
    };
};

const lazadaStore = (
    store: StoreDeclaration<LazadaStoreConfig>,
): LazadaStoreConfig => ({
    ...store,
    platform: 'lazada',
    authInfo: lazadaAuthInfoFor(store.storeId),
    printInfo: entityPrintInfo(store.entityId, store.printInfo),
});

const shopeeStore = (
    store: StoreDeclaration<ShopeeStoreConfig>,
): ShopeeStoreConfig => ({
    ...store,
    platform: 'shopee',
    authInfo: shopeeAuthInfoFor(store.storeId),
    printInfo: entityPrintInfo(store.entityId, store.printInfo),
});

const tiktokStore = (
    store: StoreDeclaration<TiktokStoreConfig>,
): TiktokStoreConfig => ({
    ...store,
    platform: 'tiktok',
    authInfo: tiktokAuthInfoFor(store.storeId),
    printInfo: entityPrintInfo(store.entityId, store.printInfo),
});

// Order is the order the combined-orders tabs render in.
//
// Every brand-facing printInfo field below is written out in full: what each
// store prints is read off its own entry, not inherited from a default and
// not diffed against another store. The taxpayer-identity fields are the
// opposite — derived from the owning entity by the factory, never declared
// here — so a store cannot print a tax registration that disagrees with its
// entityId.
//
// `address` is the one field still read from `environment`, because the two
// builds genuinely print different addresses: production carries a "(16/13-4)"
// unit number that development omits. Inlining either literal would silently
// change what one of the builds prints.
export const STORES: StoreConfig[] = [
    lazadaStore({
        storeId: 'lazada-genlogic',
        title: 'Lazada (genlogic)',
        entityId: 'pensri',
        printInfo: {
            name: 'GenLogic',
            address: environment.reportSenderAddress,
            website: 'https://www.lazada.co.th/shop/genlogic',
        },
    }),
    lazadaStore({
        storeId: 'lazada-gen4group',
        title: 'Lazada (gen4.group)',
        entityId: 'genlogic-co',
        printInfo: {
            name: 'บริษัท เจนเนอรัล ลอจิค จำกัด',
            address: environment.reportSenderAddress,
            website: 'https://www.lazada.co.th/shop/gen4-group',
        },
    }),
    shopeeStore({
        storeId: 'shopee-general4',
        title: 'Shopee (gen4)',
        entityId: 'disorn',
        platformShopId: 167942403,
        printInfo: {
            name: 'General4 Group',
            address: environment.reportSenderAddress,
            website: 'https://shopee.co.th/general4.group',
        },
    }),
    shopeeStore({
        storeId: 'shopee-genlogic',
        // disorn's store. The planned 1 September 2026 transfer to the
        // company was dropped on 2026-09-02; the company still issues the
        // tax-invoice document for explicit customer requests here
        // (requestedTaxInvoiceEntityId).
        title: 'Shopee (genlogic)',
        entityId: 'disorn',
        requestedTaxInvoiceEntityId: 'genlogic-co',
        dropOffOnly: true,
        printInfo: {
            name: 'GenLogic',
            address: environment.reportSenderAddress,
            website: 'https://shopee.co.th/genlogic',
        },
    }),
    tiktokStore({
        storeId: 'tiktok-genlogic',
        title: 'TikTok',
        entityId: 'disorn',
        printInfo: {
            name: 'GenLogic',
            address: environment.reportSenderAddress,
            website: 'https://www.tiktok.com/genlogic',
        },
    }),
];

export const LAZADA_STORES: LazadaStoreConfig[] = STORES.filter(
    (store): store is LazadaStoreConfig => store.platform === 'lazada',
);

const STORE_BY_ID = new Map<string, StoreConfig>(
    STORES.map((store): [string, StoreConfig] => [store.storeId, store]),
);

// Throws rather than returning undefined: the argument is a StoreId, so a miss
// means STORES and the StoreId union have gone out of step, and a caller that
// silently carried on would print an invoice with a blank seller.
export const storeById = (storeId: StoreId): StoreConfig => {
    const store = STORE_BY_ID.get(storeId);
    if (!store) {
        throw new Error(`No store configured for ${storeId}`);
    }
    return store;
};

export const printInfoFor = (storeId: StoreId): StorePrintInfo =>
    storeById(storeId).printInfo;

// THE seller resolver (CONSOLE-BUILD-SPEC W1). An invoice's seller is derived
// from where the sale came from — a store, or the offline counter — and is
// never chosen by an operator and never read from ambient state. Offline
// sales are `genlogic-co` by rule (spec §4.4.1): that is a constant, not a
// choice, and guard G4 refuses any row that disagrees.
export const sellerFor = (
    source: { storeId: StoreId } | { offline: true },
): SellerEntity =>
    'offline' in source
        ? entityById('genlogic-co')
        : entityById(storeById(source.storeId).entityId);

// Which taxpayer issues the TAX-INVOICE DOCUMENT for a store's order at a
// given tax-point date — a DIFFERENT question from sellerFor ("whose sale is
// it", W1), which finance attribution keeps using unchanged. The owner when
// it is registered at the date; otherwise the store's
// requestedTaxInvoiceEntityId when the customer explicitly asked for a tax
// invoice and that entity is registered. null = no registered issuer — the
// order keeps the plain invoice path.
export const taxInvoiceEntityIdFor = (
    store: StoreConfig,
    atIsoDate: string,
    hasTaxInvoiceRequest: boolean,
): EntityId | null => {
    const owner = store.entityId;
    if (isVatRegisteredAt(owner, atIsoDate)) {
        return owner;
    }
    const requested = store.requestedTaxInvoiceEntityId;
    return hasTaxInvoiceRequest &&
        requested !== undefined &&
        isVatRegisteredAt(requested, atIsoDate)
        ? requested
        : null;
};

// DI tokens naming the store a platform component is rendering for, alongside
// the auth tokens its services already read. Platform-scoped rather than a
// single 'storeId' because one page can host grids for several stores at once —
// quick-orders shows a Lazada grid and two Shopee grids in one injector tree.
export const LAZADA_STORE_ID = 'lazadaStoreId';
export const SHOPEE_STORE_ID = 'shopeeStoreId';
export const TIKTOK_STORE_ID = 'tiktokStoreId';

// Provider helpers so a page names its store exactly once: credentials and
// print identity are then guaranteed to describe the same shop.
export const lazadaStoreProviders = (storeId: LazadaStoreId): Provider[] => [
    { provide: 'lazadaAuthInfo', useValue: lazadaAuthInfoFor(storeId) },
    { provide: LAZADA_STORE_ID, useValue: storeId },
];

export const shopeeStoreProviders = (storeId: ShopeeStoreId): Provider[] => [
    { provide: 'ssmAuthInfo', useValue: shopeeAuthInfoFor(storeId) },
    { provide: SHOPEE_STORE_ID, useValue: storeId },
];

export const tiktokStoreProviders = (storeId: TiktokStoreId): Provider[] => [
    { provide: 'tiktokAuthInfo', useValue: tiktokAuthInfoFor(storeId) },
    { provide: TIKTOK_STORE_ID, useValue: storeId },
];
